import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Wallet, Users, Download, Eye, EyeOff } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { collection, query, where, onSnapshot, doc, getDoc, getDocs, setDoc } from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { FinanceDisclaimer } from '../components/finance/FinanceDisclaimer';
import { AdminStatsPanel } from '../components/finance/AdminStatsPanel';
import { OrganizerCard } from '../components/finance/OrganizerCard';
import { MoneyConfigTable } from '../components/finance/MoneyConfigTable';
import { ExportService } from '../components/finance/ExportService';
import toast from 'react-hot-toast';
import { AmountVisibilityProvider, useAmountVisibility } from '../components/finance/AmountVisibilityContext';

type OrganizerStats = {
  totalPilgrims: number;
  regularPilgrims: number;
  notOwnerPilgrims: number;
  totalAmount: number;
  bonusAmount: number;
};

type PilgrimDetails = {
  pilgrimName: string;
  passportNumber: string;
  residenceCountry?: string;
};

type FinanceConfig = {
  organizerId: string;
  organizerName: string;
  baseAmount: number;
  bonusThreshold: number;
  bonusAmount: number;
  notOwnerAmount: number;
};

// Main wrapper component for amount visibility
const OrganizerFinancePageContent = () => {
  const { translations } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastSyncInfo, setLastSyncInfo] = useState<{ timestamp: number; updatedBy: string } | null>(null);
  const [links, setLinks] = useState<Record<string, any>>({});
  const [guides, setGuides] = useState<Record<string, any>>({});
  const [organizers, setOrganizers] = useState<Record<string, any>>({});
  const [pilgrims, setPilgrims] = useState<Record<string, PilgrimDetails>>({});
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const [configRefreshTrigger, setConfigRefreshTrigger] = useState(0);
  const [financeConfig, setFinanceConfig] = useState<Record<string, FinanceConfig>>({
    default: {
      organizerId: 'default',
      organizerName: 'Default',
      baseAmount: 2000,
      bonusThreshold: 500,
      bonusAmount: 500,
      notOwnerAmount: 0
    }
  });
  const { showAmounts, toggleAmountVisibility } = useAmountVisibility();

  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');

  // Fetch finance configurations
  useEffect(() => {
    const fetchFinanceConfig = async () => {
      try {
        const configRef = collection(firestore, 'FinanceConfig');
        const configSnapshot = await getDocs(configRef);
        
        const configs: Record<string, FinanceConfig> = {};
        configSnapshot.docs.forEach(doc => {
          const configData = doc.data() as FinanceConfig;
          
          // Skip notOwner entry - it's now a column
          if (configData.organizerId === 'notOwner') {
            return;
          }
          
          // Add notOwnerAmount if not present
          if (configData.notOwnerAmount === undefined) {
            configData.notOwnerAmount = 0;
          }
          
          configs[configData.organizerId] = configData;
        });
        
        // Ensure default config exists
        if (!configs['default']) {
          configs['default'] = {
            organizerId: 'default',
            organizerName: 'Default',
            baseAmount: 2000,
            bonusThreshold: 500,
            bonusAmount: 500,
            notOwnerAmount: 0
          };
          
          // Create default config in Firestore if it doesn't exist
          await setDoc(doc(firestore, 'FinanceConfig', 'default'), configs['default']);
        }
        
        setFinanceConfig(configs);
      } catch (error) {
        console.error('Error fetching finance config:', error);
      }
    };

    fetchFinanceConfig();
  }, [configRefreshTrigger]);

  // Fetch last sync info
  useEffect(() => {
    const fetchLastSyncInfo = async () => {
      try {
        const settingsRef = doc(firestore, 'Settings', 'financeSync');
        const settingsDoc = await getDoc(settingsRef);
        const data = settingsDoc.data();
        if (settingsDoc.exists() && data?.lastSyncAt) {
          setLastSyncInfo({
            timestamp: data.lastSyncAt,
            updatedBy: data.updatedBy || 'مدير النظام'
          });
        }
      } catch (error) {
        console.error('Error fetching last sync info:', error);
      }
    };

    fetchLastSyncInfo();
  }, []);
  
  const fetchPilgrimDetails = async (pilgrimIds: string[]) => {
    try {
      setIsFetchingDetails(true);
      console.log('Fetching pilgrim details for', pilgrimIds.length, 'pilgrims');
      
      // Process pilgrim IDs in batches of 30 (Firestore limit)
      const batchSize = 30;
      const batches = [];
      
      for (let i = 0; i < pilgrimIds.length; i += batchSize) {
        batches.push(pilgrimIds.slice(i, i + batchSize));
      }
      
      const pilgrimsData = {};
      
      // Process each batch
      for (const batch of batches) {
        if (batch.length === 0) continue;
        
        const pilgrimsRef = collection(firestore, 'Pilgrims');
        const q = query(pilgrimsRef, where('__name__', 'in', batch));
        
        const snapshot = await getDocs(q);
        snapshot.forEach(doc => {
          const data = doc.data();
          pilgrimsData[doc.id] = {
            pilgrimName: data.Name || 'Unknown',
            passportNumber: data.Passport_Number || 'Unknown',
            residenceCountry: data.Residence_Country
          };
        });
      }
      
      setPilgrims(pilgrimsData);
      console.log('Fetched details for', Object.keys(pilgrimsData).length, 'pilgrims');
    } catch (error) {
      console.error('Error fetching pilgrim details:', error);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  useEffect(() => {
    const initializeData = async () => {
      try {
        setLoading(true);
        
        // Get approved links
        const linksRef = collection(firestore, 'PilgrimGuideLinks');
        let linksQuery;
        
        if (userRole === 'organizer' && userId) {
          linksQuery = query(linksRef, where('organizerId', '==', userId), where('status', '==', 'approved'));
        } else {
          linksQuery = query(linksRef, where('status', '==', 'approved'));
        }
        
        // Set up real-time listener for links
        const unsubscribeLinks = onSnapshot(linksQuery, (snapshot) => {
          const linksData = {};
          snapshot.forEach(doc => {
            linksData[doc.id] = { ...doc.data(), id: doc.id };
          });
          setLinks(linksData);
          
          // Extract pilgrim IDs to fetch their details
          const pilgrimIds = snapshot.docs.map(doc => doc.data().pilgrimId);
          fetchPilgrimDetails(pilgrimIds);
        }, error => {
          console.error('Error listening to links:', error);
        });

        // Fetch guides
        const guidesRef = collection(firestore, 'Guides');
        const guidesSnapshot = await getDocs(guidesRef);
        const guidesMap = {};
        guidesSnapshot.docs.forEach(doc => {
          guidesMap[doc.id] = { ...doc.data(), id: doc.id };
        });
        setGuides(guidesMap);

        // Fetch organizers
        const organizersRef = collection(firestore, 'Organizers');
        const organizersSnapshot = await getDocs(organizersRef);
        const organizersMap = {};
        organizersSnapshot.docs.forEach(doc => {
          organizersMap[doc.id] = { ...doc.data(), id: doc.id };
        });
        setOrganizers(organizersMap);
        
        setLoading(false);
        
        // Clean up listeners when component unmounts
        return () => {
          unsubscribeLinks();
        };
      } catch (error) {
        console.error('Error initializing data:', error);
        setError('Failed to load finance data');
        setLoading(false);
      }
    };
    
    initializeData();
  }, [userRole, userId]);

  // Get finance config for an organizer (custom or default)
  const getFinanceConfig = (organizerId: string): FinanceConfig => {
    return financeConfig[organizerId] || financeConfig['default'];
  };

  // Calculate organizer stats with the dynamic configuration
  const organizerStats = React.useMemo(() => {
    const stats: Record<string, OrganizerStats> = {};
    const organizerPilgrims: Record<string, Set<string>> = {};

    Object.values(links).forEach(link => {
      // Initialize organizer stats if not exists
      if (!stats[link.organizerId]) {
        stats[link.organizerId] = {
          totalPilgrims: 0,
          regularPilgrims: 0,
          notOwnerPilgrims: 0,
          totalAmount: 0,
          bonusAmount: 0
        };
      }
      
      // Initialize organizer pilgrims set if not exists
      if (!organizerPilgrims[link.organizerId]) {
        organizerPilgrims[link.organizerId] = new Set();
      }
      
      // Track unique pilgrims per organizer
      organizerPilgrims[link.organizerId].add(link.pilgrimId);
      
      // Update stats
      stats[link.organizerId].totalPilgrims++;
      
      // Get config for this organizer or use default
      const config = getFinanceConfig(link.organizerId);
      
      if (link.isOwner === false) {
        stats[link.organizerId].notOwnerPilgrims++;
        // Use notOwner amount from config
        stats[link.organizerId].totalAmount += config.notOwnerAmount;
      } else {
        stats[link.organizerId].regularPilgrims++;
        stats[link.organizerId].totalAmount += config.baseAmount;
      }
    });

    // Calculate bonus for organizers who meet the threshold
    Object.keys(stats).forEach(organizerId => {
      const totalPilgrims = organizerPilgrims[organizerId].size;
      const config = getFinanceConfig(organizerId);
      
      // Only apply bonus if bonusAmount > 0 AND threshold is met
      if (totalPilgrims >= config.bonusThreshold && config.bonusAmount > 0) {
        // Update total amount to include bonus for regular pilgrims
        stats[organizerId].totalAmount = stats[organizerId].regularPilgrims * (config.baseAmount + config.bonusAmount) +
                                         stats[organizerId].notOwnerPilgrims * config.notOwnerAmount;
        // Calculate total bonus amount
        stats[organizerId].bonusAmount = totalPilgrims * config.bonusAmount;
      } else {
        // Ensure bonus amount is 0 if conditions not met
        stats[organizerId].bonusAmount = 0;
      }
    });

    return stats;
  }, [links, financeConfig]);

  if (loading) return <LoadingSpinner />;

  const handleExportAll = () => {
    if (userRole !== 'admin') return;
    
    ExportService.exportAllToExcel(
      organizerStats,
      organizers,
      pilgrims,
      guides,
      links,
      translations,
      financeConfig
    );
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-50 rounded-lg">
            <Wallet className="text-primary-500" size={24} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {translations?.organizerFinance?.title || 'Organizer Finance'}
            </h1>
            <p className="text-gray-500 mt-1">
              {translations?.organizerFinance?.subtitle || 'Manage and track organizer payments'}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Toggle visibility button */}
          <button
            onClick={toggleAmountVisibility}
            className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
          >
            {showAmounts ? <EyeOff size={20} /> : <Eye size={20} />}
            <span>{showAmounts ? 'Hide Amounts' : 'Show Amounts'}</span>
          </button>
          
          {/* Export All Button for Admin */}
          {userRole === 'admin' && (
            <button
              onClick={handleExportAll}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <Download size={20} />
              <span>{translations?.organizerFinance?.exportAll || 'Export All Data'}</span>
            </button>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <FinanceDisclaimer lastSyncInfo={lastSyncInfo} />
      
      {/* Config Table - Admin Only */}
      {userRole === 'admin' && (
        <MoneyConfigTable 
          organizers={organizers}
          onConfigChange={() => setConfigRefreshTrigger(prev => prev + 1)}
        />
      )}

      {/* Admin Stats Panel */}
      {userRole === 'admin' && (
        <AdminStatsPanel organizerStats={organizerStats} />
      )}

      {/* Organizer Cards */}
      <div className="space-y-4">
        {Object.entries(organizerStats)
          .sort(([, a], [, b]) => b.totalPilgrims - a.totalPilgrims)
          .map(([organizerId, stats]) => (
            <OrganizerCard 
              key={organizerId}
              organizerId={organizerId} 
              stats={stats}
              organizers={organizers}
              links={links}
              pilgrims={pilgrims}
              guides={guides}
              isFetchingDetails={isFetchingDetails}
            />
          ))}
      </div>
    </div>
  );
};

// Wrapper component that provides the amount visibility context
export const OrganizerFinancePage = () => {
  return (
    <AmountVisibilityProvider>
      <OrganizerFinancePageContent />
    </AmountVisibilityProvider>
  );
};